// Phase 1A Phase 9A.5 — Signal time helpers.
//
// Pure deterministic Phase-1A presentation strings derived from
// `kickoffAt` and a caller-supplied `now`. Pairs with the existing
// signalStatusFromKickoff math from probability-math.ts. NO React,
// NO Supabase, NO external HTTP, NO randomness, NO clock-derived
// numerics inside the helpers themselves (callers control the
// clock).
//
// Function inventory:
//   - formatSignalCountdown(kickoffAt, now)
//   - getInvalidationCopy(status, kickoffAt)
//   - msUntilStale(kickoffAt, now)
//
// Truth contract:
//   - Status copy NEVER claims fixture cancellation, bookmaker
//     settlement, live match state, or any real-money execution.
//   - Stale = "outside the Phase-1A observation window" — a
//     window-state claim, not a fixture-state claim.

import type { SignalStatus } from "./probability-math.ts";

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const STALE_AFTER_KICKOFF_MS = 6 * MS_PER_HOUR;
const IMMINENT_THRESHOLD_MS = 60 * MS_PER_SECOND;

function parseKickoffMs(kickoffAt: string | Date | null | undefined): number | null {
  if (kickoffAt === null || kickoffAt === undefined) return null;
  const t = kickoffAt instanceof Date ? kickoffAt.getTime() : new Date(kickoffAt).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Relative-time countdown copy for a Signal Card.
 *
 *   future ≥ 24h        → "Kickoff in Xd Yh"
 *   future ≥ 1h, < 24h  → "Kickoff in Xh Ym"
 *   future ≥ 1m, < 1h   → "Kickoff in Xm Ys"
 *   future < 60s        → "Kickoff imminent"
 *   past   < 1h         → "Kickoff passed Xm ago"
 *   past   ≥ 1h, < 6h   → "Kickoff passed Xh Ym ago"
 *   past   ≥ 6h         → "Stale · 6h+ past kickoff"
 *   null / invalid      → "Kickoff time unknown"
 */
export function formatSignalCountdown(
  kickoffAt: string | Date | null | undefined,
  now: Date,
): string {
  const k = parseKickoffMs(kickoffAt);
  if (k === null) return "Kickoff time unknown";
  const nowMs = now.getTime();
  if (!Number.isFinite(nowMs)) return "Kickoff time unknown";
  const diff = k - nowMs;

  if (diff > 0) {
    if (diff < IMMINENT_THRESHOLD_MS) return "Kickoff imminent";
    if (diff < MS_PER_HOUR) {
      const m = Math.floor(diff / MS_PER_MINUTE);
      const s = Math.floor((diff - m * MS_PER_MINUTE) / MS_PER_SECOND);
      return `Kickoff in ${String(m)}m ${String(s)}s`;
    }
    if (diff < MS_PER_DAY) {
      const h = Math.floor(diff / MS_PER_HOUR);
      const m = Math.floor((diff - h * MS_PER_HOUR) / MS_PER_MINUTE);
      return `Kickoff in ${String(h)}h ${String(m)}m`;
    }
    const d = Math.floor(diff / MS_PER_DAY);
    const h = Math.floor((diff - d * MS_PER_DAY) / MS_PER_HOUR);
    return `Kickoff in ${String(d)}d ${String(h)}h`;
  }

  // Past or exactly equal to kickoff.
  const elapsed = -diff;
  if (elapsed >= STALE_AFTER_KICKOFF_MS) return "Stale · 6h+ past kickoff";
  if (elapsed >= MS_PER_HOUR) {
    const h = Math.floor(elapsed / MS_PER_HOUR);
    const m = Math.floor((elapsed - h * MS_PER_HOUR) / MS_PER_MINUTE);
    return `Kickoff passed ${String(h)}h ${String(m)}m ago`;
  }
  const m = Math.floor(elapsed / MS_PER_MINUTE);
  return `Kickoff passed ${String(m)}m ago`;
}

/**
 * Status-specific invalidation copy for a Signal Card. The status
 * is the LIVE status (re-derived at render time using ticking
 * `now`), not the fetched-at snapshot baked into the view model.
 *
 *   observing                     → "Observation window open. ..."
 *   kickoff_passed                → "Awaiting result window. ..."
 *   stale + valid kickoff         → "Outside the Phase-1A ..."
 *   stale + null/invalid kickoff  → "Kickoff time missing. ..."
 */
export function getInvalidationCopy(
  status: SignalStatus,
  kickoffAt: string | Date | null | undefined,
): string {
  if (status === "observing") {
    return "Observation window open. Re-check before use.";
  }
  if (status === "kickoff_passed") {
    return "Awaiting result window. Stale 6h after kickoff. No live match state, no bookmaker settlement.";
  }
  // status === "stale"
  const k = parseKickoffMs(kickoffAt);
  if (k === null) {
    return "Kickoff time missing. Signal cannot be observed in Phase 1A.";
  }
  return "Outside the Phase-1A observation window. Signal should not be relied on. No real-money execution.";
}

/**
 * Milliseconds remaining until the signal hits the 6-hour stale
 * boundary: (kickoffAt + 6h) − now.
 *
 * Positive while the signal is still within its observation +
 * post-kickoff window. Negative once the boundary has been crossed.
 * Returns `null` for null / invalid kickoff.
 *
 * Reserved for future polish (e.g. a thin progress bar showing
 * "stale window remaining"). Not consumed by 9A.5's UI yet, but
 * shipped + tested as part of the helper module so later phases
 * can adopt it without a follow-up commit.
 */
export function msUntilStale(
  kickoffAt: string | Date | null | undefined,
  now: Date,
): number | null {
  const k = parseKickoffMs(kickoffAt);
  if (k === null) return null;
  const nowMs = now.getTime();
  if (!Number.isFinite(nowMs)) return null;
  return k + STALE_AFTER_KICKOFF_MS - nowMs;
}
